import VideoPlayer from './VideoPlayer'
import ReactMarkdown, { Options } from 'react-markdown'
import { Link } from 'react-router'

import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import React, { ReactNode } from 'react'
import { Root } from 'react-dom/client'

const getMediaType = (url: string | undefined) => {
    if (url == undefined) return 'link'

    const imageExtensions = /\.(jpg|jpeg|png|gif|webp)/g;
    const videoExtensions = /\.(mp4|webm|avi)/g;

    if (imageExtensions.test(url)) return 'image'
    if (videoExtensions.test(url)) return 'video'

    return 'link'
}

export function getOnlyStrings(element: ReactNode, counter: number = 0) {
    if (element == null) return ""
    if (counter > 5) return ""
    if (typeof element == "string") return element

    // If it contains a property called "length" then assume the element is an array
    if (Object.prototype.hasOwnProperty.call(element, "length")) {
        const elementArray: ReactNode[] = element as ReactNode[]
        let result = ""

        for (let i = 0; i < elementArray.length; i++) {
            if (typeof elementArray[i] == "string") {
                result += elementArray[i]
                continue
            } else if ((elementArray as any)[i]['type'] == "text" && (elementArray as any)[i]['value']) {
                result += (elementArray as any)[i].value
            }
            result += getOnlyStrings(elementArray[i], counter + 1)
        }

        return result
    }

    //@ts-ignore
    const props = element.props
    if (props && props.children) {
        if (typeof props.children == "string") return props.children
        return getOnlyStrings(props.children, counter + 1)
    }

    return ""
}

function parseBody(body: string) {
    // ::::infographic\s*([\s\S]*?)::::

    const parsedInfographic = body
        .replace(
            /^\s*::::infographic\s*([\s\S]*?)::::/gm,
            (_, content) => (
                `<div id="wiki_infographic">${content.replace(/\n/g, ";")}</div>`
            )
        )

    return parsedInfographic.replace(/\n/gi, "\n\xa0\n")
}

class ScrollIntoView extends React.Component<{ id: string, children: any }> {

    componentDidMount() {
        this.scroll()
    }

    componentDidUpdate() {
        this.scroll()
    }

    scroll() {
        const { id } = this.props
        if (!id) {
            return
        }
        const element = document.querySelector(`[id='${id}']`)
        if (element) {
            const url = new URL(document.URL)
            if (url.hash.includes(id)) {
                element.scrollIntoView()
            }
        }
    }

    render() {
        return this.props.children
    }
}

export default function PostMarkdown({ body = "", className = "", rehypePlugins = [] }: { body?: string, className?: string, rehypePlugins?: any[] }) {
    function hasBreakLine(text: string) {
        return /&nbsp;/g.test(text) || /\n/g.test(text)
    }

    function getFragmentName(element: ReactNode) {
        return getOnlyStrings(element).toLocaleLowerCase().replace(/ /g, "-")
    }

    const parsedContent = parseBody(body)

    function getHeadingProps(props: Record<string, any>, textSize: string) {
        return {
            ...props,
            className: `font-semibold mb-2 ${textSize}`,
            id: getFragmentName(props.children),
            children: props.children
        }
    }

    function headings(level: number, props: Record<string, any>, textSize: string) {
        if (String(props.children).replaceAll("\xa0", "") == "") return;

        const headingProps = { ...getHeadingProps(props, textSize), node: undefined }
        return <ScrollIntoView id={getFragmentName(props.children)}>
            {[
                <h1 {...headingProps} />,
                <h2 {...headingProps} />,
                <h3 {...headingProps} />,
                <h4 {...headingProps} />,
                <h5 {...headingProps} />,
                <h6 {...headingProps} />,
            ][Math.max(Math.min(5, level - 1), 0)]}
        </ScrollIntoView>
    }

    return <ReactMarkdown
        className={className}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, ...rehypePlugins]}
        components={{
            div(props) {
                if (props.id == "wiki_infographic") {
                    const infostrings = String((props as Record<string, any>)["children"] || "").split(";")
                    let data: { type: string, value: string }[] = []
                    infostrings.forEach(item => {
                        const [_, key, value] = /(.+?):(.+)/.exec(item) || []
                        if (key && value) data.push({
                            type: key.replace(/^\s+/, ""),
                            value: value.replace(/^\s+/, "")
                        })
                    })

                    return <div className='float-right clear-right mb-3 ml-2 p-3 outline outline-1 outline-outline rounded-lg max-w-72'>
                        {data.map((item, index) => {

                            switch (item.type) {
                                case "image":
                                    return <img className='w-full' key={index} src={item.value} />

                                case "video":
                                    return <VideoPlayer
                                        key={index}
                                        className='w-full rounded-lg'
                                        src={item.value}
                                    />

                                case "title":
                                    return <h2 className='text-lg font-semibold' key={index}>{item.value}</h2>

                                case "description":
                                    return <div key={index}>
                                        <PostMarkdown body={item.value} />
                                    </div>

                                default:
                                    break;
                            }

                        })}
                    </div>
                }

                return <div {...{ ...props, node: undefined }} />
            },
            img(props) {
                // Return nothing if there's no source to use
                if (props.src == undefined) return;

                // let's just assume that it's a video when the alt says video
                if (props.alt?.toLocaleLowerCase() == "video") {
                    return <VideoPlayer
                        className='max-w-96'
                        src={props.src}
                    />
                }

                return <img src={props.src} alt={props.alt} />
            },
            a({ href, children }) {
                const mediaType = getMediaType(href);

                // let's just assume that it's a video when the alt says video
                if (String(children).toLocaleLowerCase() == "video") {
                    return <VideoPlayer
                        className='max-w-96'
                        src={href || ""}
                    />
                }

                // let's just assume that it's an image when the alt says image
                if (String(children).toLocaleLowerCase() == "image") {
                    return <img src={href} />
                }

                switch (mediaType) {
                    case "image":
                        return <img src={href} />

                    case "video":
                        return <VideoPlayer
                            className='max-w-96'
                            src={href || ""}
                        />

                    default:
                        return <Link target='_blank' className='text-primary hover:text-darkPrimary' to={href || ""} >{children}</Link>
                }

            },
            h1: (props) => headings(1, props, "text-4xl"),
            h2: (props) => headings(2, props, "text-3xl"),
            h3: (props) => headings(3, props, "text-2xl"),
            h4: (props) => headings(4, props, "text-xl"),
            h5: (props) => headings(5, props, "text-lg"),
            h6: (props) => headings(6, props, "text-base"),
            ul(props) {
                return <ul className='list-disc pl-10'>
                    {props.children}
                </ul>
            },
            li({ children, className }) {
                if (children) {
                    let content = children
                    if (typeof children == "object" && Object.prototype.hasOwnProperty.call(children, "length")) {
                        content = (children as any[]).map((item: any) => {
                            if (hasBreakLine(item)) return undefined;
                            return item
                        })
                    }
                    return <li className={className}>{content}</li>
                }

                return <li className={className}></li>
            },
            code({ children, className }) {
                return <div className='overflow-x-auto'>
                    <code className={'p-1 px-2 my-2 bg-outline inline-block ' + className}>
                        {typeof children == "string"
                            ? children.split("\xa0 ")
                                .map((text, index) => (<span key={index} className="block" >{text}</span>))
                            : children
                        }
                    </code>
                </div>
            },
            p({ children, className }) {
                if (children) {
                    let content = children
                    if (typeof children == "object" && Object.prototype.hasOwnProperty.call(children, "length")) {
                        let removedLine = false
                        content = (children as any[]).map((item: any, index: number) => {
                            if (String(item) == "\n\xa0" || String(item) == "\n " || String(item) == "\xa0") return undefined;
                            
                            if (
                                (item["type"] == "br" && removedLine == false) ||
                                (
                                    (index + 1) < (children as any[]).length
                                    && String((children as any[])[index + 1]) == "\n\xa0"
                                )
                            ) {
                                removedLine = true;
                                return undefined;
                            }
                            return item
                        })
                    }
                    return <span className={'block ' + (className ? className : "")}>{content}</span>
                }
                return <span className={'block ' + (className ? className : "")}></span>
            },
            meta(props) {
                return null
            },
            script(props) {
                return null
            }
        }}
        children={parsedContent}
    />
}
